import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useDispatch, useSelector } from "react-redux";
import {mainSelector, AGModalOpen, MenuModalOpen, InfoModalOpen} from "../../store/slices/main.slice";
import Button from "../layout/Button";
import Draggable from 'react-draggable';
import HamburgerMenu from "../layout/HamburgerMenu";
import { isMobile, isTablet } from 'react-device-detect';
import QRScanner from "../QRScanner";
import {useTranslation} from "react-i18next";
import {apiService} from "../../services/api.service";
import {parseText} from "../../utils/transaltions.utils";

const AGModalRoot = ({ children }: any) => {
    const { t, i18n }: any = useTranslation('translation');
    const dispatch: any = useDispatch();
    const { AGmodal } = useSelector(mainSelector);
    const [data, setData] = useState<any>(null)
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
        if(AGmodal.open && AGmodal.id) {
            setIsLoading(true)
            apiService.getAudioguida(AGmodal.id)
                .then((res) => {
                    setData(res.data)
                    setIsLoading(false)
                })
                .catch((err) => {
                    console.error('Error fetching audioguida:', err);
                    setIsLoading(false);
                })
        } else {
            setData(null)
            setIsLoading(true)
        }
    }, [AGmodal.open, AGmodal.id]);

    const [showScrollUp, setShowScrollUp] = useState(false);
    const [showScrollDown, setShowScrollDown] = useState(false);
    const scrollableDivRef = useRef<HTMLParagraphElement | null>(null);
    const scrollableDivRefMobile = useRef<HTMLParagraphElement | null>(null);

    const checkScroll = () => {
        const div = (isMobile || isTablet ? scrollableDivRefMobile.current : scrollableDivRef.current) as HTMLElement | null;
        if (!div) return;
        const maxScroll = div.scrollHeight - div.clientHeight;
        setShowScrollUp(div.scrollTop > 0);
        setShowScrollDown(div.scrollTop < maxScroll);
    };

    useEffect(() => {
        if (!AGmodal.open || !data) return;
        const el = (isMobile || isTablet ? scrollableDivRefMobile.current : scrollableDivRef.current) as HTMLElement | null;
        if (!el) return;
        const run = () => checkScroll();
        run();
        const raf1 = requestAnimationFrame(run);
        const raf2 = requestAnimationFrame(run);
        const ro = new ResizeObserver(run);
        ro.observe(el);
        const tm = setTimeout(run, 600);
        return () => {
            cancelAnimationFrame(raf1);
            cancelAnimationFrame(raf2);
            ro.disconnect();
            clearTimeout(tm);
        };
    }, [AGmodal.open, data, i18n.language]);

    const scrollDown = () => {
        const el = (isMobile || isTablet ? scrollableDivRefMobile.current : scrollableDivRef.current) as HTMLElement | null;
        if (el) el.scrollBy({ top: 100, behavior: 'smooth' });
    };

    const scrollUp = () => {
        const el = (isMobile || isTablet ? scrollableDivRefMobile.current : scrollableDivRef.current) as HTMLElement | null;
        if (el) el.scrollBy({ top: -100, behavior: 'smooth' });
    };

    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const videoRefMobile = useRef<HTMLVideoElement | null>(null);
    const progressRef: any = useRef(null);
    const progressRefMobile: any = useRef(null);

    const handlePlayPause = () => {
        const video = (isMobile || isTablet ? videoRefMobile.current : videoRef.current) as HTMLVideoElement | null;
        if (!video) return;
        if (isPlaying) {
            video.pause();
        } else {
            video.play();
        }
        setIsPlaying(!isPlaying);
    };

    const handleTimeUpdate = () => {
        const video = (isMobile || isTablet ? videoRefMobile.current : videoRef.current) as HTMLVideoElement | null;
        if (!video) return;
        const currentTime = video.currentTime || 0;
        const duration = video.duration || 0;
        const progressPercent = duration ? (currentTime / duration) * 100 : 0;
        setProgress(progressPercent);
    };

    const handleLoadedMetadata = () => {
        const video = (isMobile || isTablet ? videoRefMobile.current : videoRef.current) as HTMLVideoElement | null;
        if (!video) return;
        // Su iOS Safari, impostiamo currentTime a 0.1 per forzare il caricamento del primo frame
        if (video.currentTime === 0) {
            video.currentTime = 0.1;
        }
    };

    const handleDrag = (e: any, data: any) => {
        const video = (isMobile || isTablet ? videoRefMobile.current : videoRef.current) as HTMLVideoElement | null;
        if (!video) return;
        const progressBar: any = progressRef.current;
        if (!progressBar) return;
        const newProgress = (data.x / progressBar.offsetWidth) * 100;
        if (newProgress < 0 || newProgress > 100) return;
        const newTime = (newProgress / 100) * video.duration;
        if (!isNaN(newTime) && isFinite(newTime)) {
            video.currentTime = newTime;
            setProgress(newProgress);
        }
    };

    const onClose = () => {
        dispatch(AGModalOpen(false))
        setIsTitleScrolled(false)
        setIsScrolled(false)
        setProgress(0)
        setIsPlaying(false)
        setData(null)
        setIsLoading(true)
    }

    const goToHome = () => {
        dispatch(AGModalOpen(false))
        window.location.href = `/`
    }

    const openInfo = () => {
        dispatch(MenuModalOpen(false))
        dispatch(InfoModalOpen(true))
    }

    const openMenu = () => {
        dispatch(MenuModalOpen(true))
    }

    const divRef = useRef<HTMLDivElement | null>(null);
    const [isScrolled, setIsScrolled] = useState(false);
    const [isTitleScrolled, setIsTitleScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            if (divRef.current) {
                const { scrollTop } = divRef.current;
                setIsScrolled(scrollTop > 0);
                setIsTitleScrolled(scrollTop > 65);
            }
        };

        // Wait for data to be loaded before attaching scroll listener
        if (!data) return;

        const divElement = divRef.current;
        if (divElement) {
            divElement.addEventListener('scroll', handleScroll);
        }

        // Clean up the event listener on component unmount
        return () => {
            if (divElement) {
                divElement.removeEventListener('scroll', handleScroll);
            }
        };
    }, [divRef, AGmodal, data]);

    const handleTouchStart = (e: any) => {
        document.addEventListener('touchmove', handleTouchMove);
        document.addEventListener('touchend', handleTouchEnd);
        handleSeek(e.touches[0]);
    };

    const handleTouchMove = (e: any) => {
        handleSeek(e.touches[0]);
    };

    const handleTouchEnd = () => {
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
    };

    const handleSeek = (touch: any) => {
        const video = (isMobile || isTablet ? videoRefMobile.current : videoRef.current) as HTMLVideoElement | null;
        const progressBar = progressRefMobile.current || progressRef.current;
        if (!progressBar || !video) return;
        const rect = progressBar.getBoundingClientRect();
        const offsetX = touch.clientX - rect.left;
        const newProgress = (offsetX / rect.width) * 100;
        if (newProgress >= 0 && newProgress <= 100) {
            const newTime = (newProgress / 100) * video.duration;
            if (isFinite(newTime)) {
                video.currentTime = newTime;
                setProgress(newProgress);
            }
        }
    };

    const [scanning, setScanning] = useState(false);

    const handleScanResult = (data: string) => {
        if (data) {
            window.location.href = data;
        }
    };

    const startScanner = () => {
        setScanning(true);
    };

    const stopScanner = () => {
        setScanning(false);
    };

    if(isLoading) {
        return null
    }

    if(data){
        return (
            <AnimatePresence>
                {AGmodal?.open && (
                    <>
                        <motion.div
                            key="AGmodal-root"
                            className="hidden lg:flex fixed top-0 left-0 w-full h-full bg-black/85 justify-center items-center z-[900]"
                            initial={{opacity: 0}}
                            animate={{opacity: 1}}
                            exit={{opacity: 0, transition: {duration: 0.5}}}
                            onClick={onClose}
                        >
                            <header className="hidden md:flex absolute top-0 left-0 flex-row items-center justify-between w-full gap-[50px] px-[40px] h-[84px]">
                                <div>
                                    <img src="/images/GNDM logo full.png" alt="Header GNDM Logo" className="max-h-[30px]"/>
                                </div>
                            </header>
                            <motion.div
                                key="AGmodal-content"
                                className="p-6 rounded-lg w-[90vw] md:w-[70vw]"
                                initial={{opacity: 0}}
                                animate={{opacity: 1, transition: {duration: 0.5, delay: 0}}}
                                exit={{opacity: 0, transition: {duration: 0.5}}}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="flex flex-col md:flex-row gap-[10px] md:gap-[50px]">
                                    <div className="md:hidden flex flex-col w-full items-center h-max mb-[-20px]">
                                        <button onClick={onClose} className="text-white text-md font-untitledSans">X</button>
                                    </div>
                                    <section className="w-full md:w-1/2">
                                        <div style={{backgroundImage: `url('${data.backgroundImage}')`}} className="bg-no-repeat bg-cover bg-center px-[30px] py-[40px]">
                                            <video
                                                ref={videoRef}
                                                onTimeUpdate={handleTimeUpdate}
                                                onLoadedMetadata={handleLoadedMetadata}
                                                preload="metadata"
                                            >
                                                <source src={parseText(data.video, i18n)} type="video/mp4"/>
                                                Your browser does not support the video tag.
                                            </video>
                                            <div className="flex flex-row items-center gap-[10px] mt-[20px]">
                                                <button
                                                    onClick={handlePlayPause}
                                                    className="cursor-pointer"
                                                >
                                                    <img src={isPlaying ? `/images/pause-icon.png` : `/images/play-icon.png`} alt="Play icon" className="h-[17px]"/>
                                                </button>
                                                <div ref={progressRef} className="relative bg-white h-[5px] w-full">
                                                    <div
                                                        className="bg-[#ec651f] h-full"
                                                        style={{width: `${progress}%`}}
                                                    ></div>
                                                    <Draggable
                                                        axis="x"
                                                        bounds="parent"
                                                        position={{x: (progress / 100) * (progressRef.current?.offsetWidth || 0), y: 0}}
                                                        onDrag={handleDrag}
                                                    >
                                                        <div className="absolute top-[-6px] w-[15px] h-[15px] bg-[#ec651f] rounded-full cursor-pointer"></div>
                                                    </Draggable>
                                                </div>
                                            </div>
                                        </div>
                                    </section>
                                    <section className="overflow-hidden w-full md:w-1/2 mt-[-66px]">
                                        <div className="mb-[20px] h-max flex flex-row justify-end items-end">
                                            <button onClick={onClose} className="hidden md:block text-white text-md font-untitledSans">X</button>
                                        </div>
                                        <div className="max-h-[450px]">
                                            <h2 className="text-white text-md md:text-lg font-untitledSans font-bold">{parseText(data.title, i18n)}</h2>
                                            <span className="text-white text-xs font-untitledSans">#{parseText(data.category, i18n)}</span>
                                            <div className="h-[30px]"/>
                                            <p onScroll={checkScroll} ref={scrollableDivRef} className="scrollable-div text-white text-sm font-untitledSans text-justify h-[275px] overflow-y-auto">{parseText(data.content, i18n)}</p>
                                        </div>
                                        <section className="mt-[20px] flex flex-row items-center justify-between">
                                            {!showScrollUp && <div/>}
                                            {showScrollUp && <button onClick={scrollUp} className="text-white text-xs font-untitledSans font-bold h-[20px] cursor-pointer">{t('ag.global_read_less')}</button>}
                                            {showScrollDown && <button onClick={scrollDown} className="text-white text-xs font-untitledSans font-bold h-[20px] cursor-pointer">{t('ag.global_read_more')}</button>}
                                        </section>
                                        <div className="w-full flex flex-col items-center mt-[30px]">
                                            {data.externalLink && data.externalLink !== "" && <Button text={t('ag.global_external_link')} onClick={null}/>}
                                        </div>
                                    </section>
                                </div>
                            </motion.div>
                        </motion.div>
                        <motion.div
                            key="AGmodal-root-mobile"
                            className="lg:hidden flex fixed top-0 left-0 w-full h-full bg-[#2f2e2a] z-[902]"
                            initial={{opacity: 0}}
                            animate={{opacity: 1}}
                            exit={{opacity: 0, transition: {duration: 0.5}}}
                        >
                            <motion.div
                                ref={divRef}
                                key="relative AGmodal-content-mobile"
                                className="rounded-lg w-full h-full overflow-y-auto"
                                initial={{opacity: 0}}
                                animate={{opacity: 1, transition: {duration: 0.5, delay: 0}}}
                                exit={{opacity: 0, transition: {duration: 0.5}}}
                                onClick={(e) => e.stopPropagation()}
                            >
                                {scanning && <QRScanner onScan={handleScanResult} onClose={stopScanner}/>}
                                <header className={`px-[20px] ${isScrolled ? 'bg-black' : 'bg-transparent'} h-[84px] sm:h-[60px] absolute top-0 left-0 flex flex-row items-center justify-between w-full gap-[50px] mb-[30px] pr-[20px] z-[999]`}>
                                    <div>
                                        <img onClick={goToHome} src="/images/GNDM acronimo orizzontale white.png" alt="Header GNDM Logo" className="max-h-[20px] cursor-pointer lg:hidden"/>
                                        <img onClick={goToHome} src='/images/GNDM logo full.png' alt="Header GNDM Logo" className="max-h-[30px] cursor-pointer hidden lg:block"/>
                                    </div>
                                    <div className="flex flex-row items-center h-max gap-[50px] mr-[20px]">
                                        <button onClick={openInfo} className="w-[10px] h-[25px] min-w-[10px] min-h-[25px] flex flex-col items-center justify-center cursor-pointer"><img src='/images/info-icon.png' alt="Info icon" className="h-full"/></button>
                                        <div className="relative"><HamburgerMenu version="white" onClick={openMenu}/></div>
                                    </div>
                                </header>
                                {isTitleScrolled && <div className="z-[999] w-full flex flex-row gap-[10px] justify-between items-center py-[5px] px-[20px] absolute top-[84px] sm:top-[60px] left-0 bg-[#e4dbd6]">
                                    <p className="text-black text-sm font-untitledSans font-bold">{parseText(data.title, i18n)}</p>
                                    <button onClick={onClose} className="text-black font-untitledSans cursor-pointer w-full w-max font-bold text-[20px]">X</button>
                                </div>}
                                <div className="flex flex-col lg:flex-row gap-[20px] lg:gap-[50px]">
                                    <section className="w-full lg:w-1/2">
                                        <div style={{backgroundImage: `url('${data.backgroundImage}')`}} className="pt-[90px] sm:pt-[80px] bg-no-repeat bg-cover bg-center px-[20px] pb-[40px]">
                                            <button onClick={onClose} className="text-white text-md font-bold font-untitledSans cursor-pointer my-[10px] text-right w-full">X</button>
                                            <h2 className="text-white text-base font-untitledSans font-bold">{parseText(data.title, i18n)}</h2>
                                            <span className="text-white text-xxs font-untitledSans">#{parseText(data.category, i18n)}</span>
                                            <div className="h-[10px]"/>
                                            <video
                                                ref={videoRefMobile}
                                                onTimeUpdate={handleTimeUpdate}
                                                onLoadedMetadata={handleLoadedMetadata}
                                                playsInline
                                                preload="metadata"
                                            >
                                                <source src={parseText(data.video, i18n)} type="video/mp4"/>
                                                Your browser does not support the video tag.
                                            </video>
                                            <div className="flex flex-row items-center gap-[10px] mt-[20px]">
                                                <button
                                                    onClick={handlePlayPause}
                                                    className="cursor-pointer"
                                                >
                                                    <img src={isPlaying ? `/images/pause-icon.png` : `/images/play-icon.png`} alt="Play icon" className="h-[17px]"/>
                                                </button>
                                                <div ref={progressRefMobile} className="relative bg-white h-[5px] w-full" onTouchStart={handleTouchStart}>
                                                    <div
                                                        className="bg-[#ec651f] h-full"
                                                        style={{width: `${progress}%`}}
                                                    ></div>
                                                    <Draggable
                                                        axis="x"
                                                        bounds="parent"
                                                        position={{x: (progress / 100) * (progressRefMobile.current?.offsetWidth || 0), y: 0}}
                                                        onDrag={handleDrag}
                                                        defaultClassNameDragging="touch-action-none"
                                                    >
                                                        <div className="absolute top-[-6px] w-[15px] h-[15px] bg-[#ec651f] rounded-full cursor-pointer"></div>
                                                    </Draggable>
                                                </div>
                                            </div>
                                        </div>
                                    </section>
                                    <section className="overflow-hidden w-full px-[20px] lg:w-1/2 pb-[140px]">
                                        <div className="max-h-[400px]">
                                            <p onScroll={checkScroll} ref={scrollableDivRefMobile} className="scrollable-div text-white text-sm font-untitledSans text-justify h-[400px] overflow-y-auto">{parseText(data.content, i18n)}</p>
                                        </div>
                                        <section className="mt-[20px] flex flex-row items-center justify-between">
                                            {!showScrollUp && <div/>}
                                            {showScrollUp && <button onClick={scrollUp} className="text-white text-xs font-untitledSans font-bold h-[20px] cursor-pointer">{t('ag.global_read_less')}</button>}
                                            {showScrollDown && <button onClick={scrollDown} className="text-white text-xs font-untitledSans font-bold h-[20px] cursor-pointer">{t('ag.global_read_more')}</button>}
                                        </section>
                                        <div className="w-full flex flex-col items-center mt-[20px]">
                                            {data.externalLink && data.externalLink !== "" && <Button text={t('ag.global_external_link')} onClick={null}/>}
                                        </div>
                                    </section>
                                    <div className="flex sm:hidden fixed left-0 bottom-0 z-[900] w-full bg-black/95 h-[100px] flex-row items-center justify-center px-[20px]">
                                        {!scanning && <div onClick={startScanner} className="flex flex-col items-center gap-[7px] cursor-pointer">
                                            <img src={`/images/icona-inquadra.png`} alt="Inquadra-icona" className="h-[30px]"/>
                                            <span className="text-white text-xs text-center font-untitledSans">{t('ag.global_inquadra')}</span>
                                        </div>}
                                    </div>
                                    <div className="hidden sm:flex lg:hidden fixed left-0 bottom-0 z-[900] w-full bg-black/95 h-[60px] flex-row items-center justify-center px-[20px]">
                                        {!scanning && <div onClick={startScanner} className="flex flex-row items-center gap-[10px] cursor-pointer">
                                            <img src={`/images/icona-inquadra.png`} alt="Inquadra-icona" className="h-[30px]"/>
                                            <span className="text-white text-xs text-center font-untitledSans">{t('ag.global_inquadra')}</span>
                                        </div>}
                                    </div>
                                </div>
                            </motion.div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        );
    }

    return null
};

export default AGModalRoot;